import { Button } from '#/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '#/components/ui/drawer';
import { cn } from '#/lib/utils';
import { useState } from 'react';

import type { ApiV2MallKabuandUpdateUserInfoResponse } from '~/api/v2/mall/kabuand/update-user-info';
import { DATA_AREAS } from '~/constants/data-areas';
import { addComma } from '~/utils';

import type { GlobalConfigHeader } from '../api/global-config';
import { buttonStyles } from '../styles';
import SvgIconStockAmount from '../svg/icon-stock-amount';
import SvgImgExchangeTicket from '../svg/img-exchange-ticket';
import { SiteHeaderLocalNav } from './site-header-local-nav';
import { LoginButton } from './site-login-button';

export const SiteHeader = (props: {
  globalConfigHeader: GlobalConfigHeader;
  isLogoOnly?: boolean;
  isLoggedIn?: boolean;
  userInfo?: ApiV2MallKabuandUpdateUserInfoResponse;
}) => {
  const { globalConfigHeader, isLogoOnly, isLoggedIn = false, userInfo } = props;

  const [drawerOpen, setDrawerOpen] = useState(false);

  const logo = globalConfigHeader.logo;
  const serviceMenus = (!isLogoOnly && globalConfigHeader.serviceMenus) || [];
  const loginButton = !isLogoOnly && globalConfigHeader.loginButton;
  const subMenus =
    !isLogoOnly && isLoggedIn ? globalConfigHeader.menusIfLogin : globalConfigHeader.menusIfNotLogin || [];
  const bottomMenus =
    !isLogoOnly && isLoggedIn
      ? globalConfigHeader.bottomMenusIfLogin || []
      : globalConfigHeader.bottomMenusIfNotLogin || [];

  return (
    <div className="sticky top-0 isolate z-10" data-area={DATA_AREAS.SITE_HEADER}>
      <div className="z-50 w-full overflow-hidden bg-white">
        <div className="flex items-center px-4 py-[6px] lg:px-8 lg:py-[14px]">
          <a href={`${import.meta.env.VITE_APP_KABUAND_URL}`} className="flex h-9 items-center" aria-label={logo.alt}>
            <img alt={logo.alt} width={74} height={36} decoding="auto" src={logo.src} />
          </a>
          {!isLogoOnly && (
            <div className="flex flex-1 items-center justify-end gap-3">
              {isLoggedIn ? (
                <>
                  {userInfo && (
                    <a
                      className="border-grey-200 flex items-center rounded-full border px-1"
                      href={`${import.meta.env.VITE_APP_KABUAND_URL}${globalConfigHeader.point.href}`}
                      target="_blank"
                      rel="noopener noreferrer">
                      <span className="flex items-center space-x-1 px-2">
                        <SvgImgExchangeTicket aria-label="株引換券" />
                        <span className="text-caption-m text-grey-900">{addComma(userInfo.display_balance)}枚</span>
                      </span>
                      <span className="border-grey-200 h-3 border-r"></span>
                      <span className="flex items-center space-x-1 px-2">
                        <SvgIconStockAmount aria-label="株" />
                        <span className="text-caption-m text-grey-900">{addComma(userInfo.stock_amount)}株</span>
                      </span>
                    </a>
                  )}
                </>
              ) : (
                <LoginButton className="text-caption-s-bold hover:text-brand rounded-full transition-colors">
                  {loginButton && loginButton.text}
                </LoginButton>
              )}
              <Drawer direction="right" handleOnly={true} open={drawerOpen} onOpenChange={setDrawerOpen}>
                <DrawerTrigger asChild onClick={(e) => e.currentTarget.blur()}>
                  <Button
                    className="group relative h-auto w-auto border-none shadow-none hover:bg-transparent"
                    variant="outline"
                    size="icon"
                    aria-label="ハンバーガーメニュー">
                    <span className="group flex h-[24px] w-[24px] flex-col justify-between px-[3px] py-1">
                      <i className="text-grey-900 bg-grey-900 group-hover:bg-main block h-[2px] w-full group-hover:bg-red-500" />
                      <i className="text-grey-900 bg-grey-900 group-hover:bg-main block h-[2px] w-full group-hover:bg-red-500" />
                      <i className="text-grey-900 bg-grey-900 group-hover:bg-main block h-[2px] w-full group-hover:bg-red-500" />
                    </span>
                  </Button>
                </DrawerTrigger>
                <DrawerContent
                  overlayClassName="bg-transparent"
                  className={cn(
                    'bg-white pb-10 data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-full data-[vaul-drawer-direction=right]:md:max-w-[400px]'
                  )}>
                  <DrawerTitle className="sr-only">グローバルメニュー</DrawerTitle>
                  <DrawerDescription className="sr-only">グローバルメニュー</DrawerDescription>
                  <div className="relative flex h-full flex-col overflow-hidden">
                    <div
                      className={cn(
                        `flex px-4 ${isLoggedIn ? 'justify-end pt-3' : 'border-grey-100 bg-grey-50 flex-col items-center gap-1 border-b py-3'}`
                      )}>
                      <DrawerClose asChild>
                        <button type="button" aria-label="Close" className="relative place-self-end">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="text-grey-950 hover:text-brand size-6 flex-none transition-colors">
                            <path
                              d="M19.6482 3L12.0002 10.6482L4.35226 3L3 4.35182L10.6485 12L3 19.6482L4.35226 21L12.0002 13.3518L19.6482 21L21 19.6482L13.352 12L21 4.35182L19.6482 3Z"
                              fill="currentColor"></path>
                          </svg>
                        </button>
                      </DrawerClose>
                      {!isLoggedIn && (
                        <>
                          <LoginButton className={buttonStyles.default.lg} onClick={() => setDrawerOpen(false)}>
                            ログイン
                          </LoginButton>
                        </>
                      )}
                    </div>
                    <div className="flex grow flex-col gap-10 overflow-y-auto px-12 py-8">
                      <div className="text-grey-600 relative grow">
                        <div
                          className={cn(
                            'divide-grey-100 flex grow divide-y',
                            isLoggedIn ? 'flex-col-reverse divide-y-reverse' : 'flex-col'
                          )}>
                          {serviceMenus && serviceMenus.length > 0 && (
                            <ul className={cn('flex flex-col gap-3', isLoggedIn ? 'pt-8' : 'pb-8')}>
                              {serviceMenus.map((data) => (
                                <li
                                  key={data.label}
                                  className="text-headline-xs hover:text-brand transition-colors hover:list-disc">
                                  <a
                                    href={`${import.meta.env.VITE_APP_KABUAND_URL}${data.href}`}
                                    aria-label={data.label}>
                                    {data.label}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                          {subMenus && subMenus.length > 0 && (
                            <ul className={cn('flex flex-col gap-3', isLoggedIn ? 'pb-8' : 'pt-8')}>
                              {subMenus.map((data) => (
                                <li
                                  key={data.label}
                                  className="text-headline-xs hover:text-brand transition-colors hover:list-disc">
                                  <a
                                    href={`${import.meta.env.VITE_APP_KABUAND_URL}${data.href}`}
                                    aria-label={data.label}>
                                    {data.label}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      {bottomMenus && bottomMenus.length > 0 && (
                        <ul className="flex flex-wrap gap-6 bg-white">
                          {bottomMenus.map((data) => {
                            // ログアウトの場合は特別な処理
                            if (data.href === '/account/logout') {
                              const targetUrl = `${import.meta.env.VITE_APP_KABUAND_URL}${data.href}`;
                              const logoutUrl = `/logout?transition_url=${encodeURIComponent(targetUrl)}`;
                              return (
                                <li
                                  key={data.label}
                                  className="text-caption-m-bold text-grey-400 hover:text-grey-950 transition-colors">
                                  <a href={logoutUrl} onClick={() => setDrawerOpen(false)} aria-label={data.label}>
                                    {data.label}
                                  </a>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={data.label}
                                className="text-caption-m-bold text-grey-400 hover:text-grey-950 transition-colors">
                                <a href={`${import.meta.env.VITE_APP_KABUAND_URL}${data.href}`} aria-label={data.label}>
                                  {data.label}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </DrawerContent>
              </Drawer>
            </div>
          )}
        </div>
      </div>
      {!isLogoOnly && <SiteHeaderLocalNav />}
    </div>
  );
};
